import React from 'react';
import { TableComparison, SchemaChange } from '../../hooks/useDeltaCompare';
import { DualScrollTable, usePagination, PaginationControls } from '../../../../components/common/TableControls';

interface CompareResultsTableProps {
  tables: TableComparison[];
  isLoading?: boolean;
}

const getChangeIcon = (changeType: SchemaChange['change_type']) => {
  switch (changeType) {
    case 'column_added':
      return (
        <svg
          className='h-4 w-4 text-green-500'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
        </svg>
      );
    case 'column_removed':
      return (
        <svg className='h-4 w-4 text-red-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 12H4' />
        </svg>
      );
    case 'type_changed':
    case 'column_modified':
      return (
        <svg
          className='h-4 w-4 text-yellow-500'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z'
          />
        </svg>
      );
  }
};

export const CompareResultsTable: React.FC<CompareResultsTableProps> = ({ tables, isLoading }) => {
  const [expandedTable, setExpandedTable] = React.useState<string | null>(null);
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(tables);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500'></div>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className='text-center py-12'>
        <svg
          className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
          />
        </svg>
        <h3 className='mt-2 text-sm font-medium text-[var(--color-text-primary)]'>No comparison results</h3>
        <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>Run a comparison to see table differences.</p>
      </div>
    );
  }

  // Spec 298 FR-006: an unknown count reads "—", never 0
  const formatNumber = (num: number | null): string => (num === null ? '—' : new Intl.NumberFormat().format(num));

  const getDiffColor = (diff: number | null): string => {
    if (diff === null) return 'text-[var(--color-text-secondary)]';
    if (diff > 0) return 'text-green-600 dark:text-green-400';
    if (diff < 0) return 'text-red-600 dark:text-red-400';
    return 'text-[var(--color-text-secondary)]';
  };

  return (
    <>
    <DualScrollTable>
      <table className='min-w-full divide-y divide-[var(--color-border)]'>
        <thead className='bg-[var(--color-bg-subtle)]'>
          <tr>
            <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Table Name
            </th>
            <th className='px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Backup Rows
            </th>
            <th className='px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Current Rows
            </th>
            <th className='px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Difference
            </th>
            <th className='px-6 py-3 text-center text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Schema
            </th>
            <th className='px-6 py-3 text-center text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
              Details
            </th>
          </tr>
        </thead>
        <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
          {pageItems.map((table) => (
            <React.Fragment key={table.table_name}>
              <tr className='hover:bg-[var(--color-bg-subtle)]'>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div className='text-sm font-medium text-[var(--color-text-primary)]'>{table.table_name}</div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-right text-sm text-[var(--color-text-primary)]'>
                  {formatNumber(table.backup_row_count)}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-right text-sm text-[var(--color-text-primary)]'>
                  {formatNumber(table.current_row_count)}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-right'>
                  <div className='flex items-center justify-end gap-2'>
                    <span className={`text-sm font-medium ${getDiffColor(table.row_count_diff)}`}>
                      {table.row_count_diff !== null && table.row_count_diff > 0 ? '+' : ''}
                      {formatNumber(table.row_count_diff)}
                    </span>
                    {table.row_count_diff !== 0 && table.row_count_diff_pct !== null && (
                      <span className='text-xs text-[var(--color-text-secondary)]'>
                        ({table.row_count_diff_pct > 0 ? '+' : ''}
                        {table.row_count_diff_pct.toFixed(1)}%)
                      </span>
                    )}
                  </div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-center'>
                  {table.schema_changed ? (
                    <span className='px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'>
                      Changed
                    </span>
                  ) : (
                    <span className='px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'>
                      Same
                    </span>
                  )}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-center'>
                  {table.schema_changes && table.schema_changes.length > 0 && (
                    <button
                      onClick={() =>
                        setExpandedTable(
                          expandedTable === table.table_name ? null : table.table_name
                        )
                      }
                      className='text-[var(--color-accent)] hover:opacity-80'
                    >
                      {expandedTable === table.table_name ? (
                        <svg
                          className='h-5 w-5'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 15l7-7 7 7'
                          />
                        </svg>
                      ) : (
                        <svg
                          className='h-5 w-5'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                          />
                        </svg>
                      )}
                    </button>
                  )}
                </td>
              </tr>
              {expandedTable === table.table_name && table.schema_changes && (
                <tr>
                  <td colSpan={6} className='px-6 py-4 bg-[var(--color-bg-subtle)]'>
                    <div className='text-sm'>
                      <h4 className='font-medium text-[var(--color-text-primary)] mb-2'>Schema Changes:</h4>
                      <ul className='space-y-2'>
                        {table.schema_changes.map((change, idx) => (
                          <li key={idx} className='flex items-start gap-2'>
                            {getChangeIcon(change.change_type)}
                            <div className='flex-1'>
                              <div className='font-medium text-[var(--color-text-primary)]'>{change.column_name}</div>
                              <div className='text-[var(--color-text-secondary)]'>{change.description}</div>
                              {change.old_value && change.new_value && (
                                <div className='text-xs text-[var(--color-text-secondary)] mt-1'>
                                  <span className='line-through'>{change.old_value}</span>
                                  {' → '}
                                  <span>{change.new_value}</span>
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </DualScrollTable>
    <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
    </>
  );
};
